/**
 * HotelConfirmationPage.jsx: full-page hotel booking voucher, at /hotel-booking/:rcBookingId
 *
 * Shown after paying (HotelBookingPage hands off here), from Booking History (view / cancel),
 * and after a cancellation. Everything comes from one get_status call: the booking row, which
 * carries TBO's GetBookingDetail (booking_detail), PreBook, the change request and refund.
 *
 * States (row.status):
 *   BOOKING_CONFIRMED                          confirmed voucher, cancel available until check-in
 *   PAYMENT_SUCCESS / BOOKING_IN_PROGRESS /
 *   BOOKING_PENDING / VOUCHER_IN_PROGRESS      confirming with the hotel (polls)
 *   HOLD_CONFIRMED                             reserved, not paid: pay-by and "Pay & confirm"
 *   HOLD_IN_PROGRESS / HOLD_PENDING            reserving (polls)
 *   CANCELLATION_REQUESTED / _VERIFYING /
 *   _IN_PROGRESS                               cancellation under way (polls; refund tracker)
 *   CANCELLED                                  cancelled, refund tracker
 *   HOLD_RELEASED / HOLD_EXPIRED               reservation released, nothing charged
 *   *_FAILED                                   not completed, refund note
 *   PREBOOKED / PAYMENT_PENDING                payment not completed
 * While the backend has a TBO check scheduled (verify_kind, e.g. GetBookingDetail 120s after a
 * cancellation), the page keeps polling and then shows "Verified with the hotel".
 *
 * ?cancel=1 opens the cancel panel straight away (Booking History's cancel button).
 * Owner for get_status: router state, then sessionStorage (set by HotelBooking), then login.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import ContactSupportModal from "../components/bookingFlight/ContactSupportModal";
import CoBrandBar from "../components/partner/CoBrandBar";
import { usePartnerBrand } from "../utils/partnerBrand";
import { getCurrentContact } from "../utils/currentIdentity";
import { FEATURES, hotelOrchestratorUrl } from "../config/features";

const HOTEL_TEST = FEATURES.HOTEL_TEST ?? true;
const OWNER_KEY = "rc_hotel_owner_";          // written by HotelBooking.jsx
export const hotelConfirmationUrl = (rcId) => `/hotel-booking/${encodeURIComponent(rcId)}`;

/* ── Tokens (same as the booking page) ── */
const ACCENT = "#E8540C";
const ACCENT_DARK = "#C94707";
const INK = "#1A1A1A";
const MUTED = "#5F6368";
const LINE = "#E3E3E3";
const OK = "#137333";
const BAD = "#B3261E";
const WARN = "#B26A00";
const INFO = "#1A5FB4";
const IST = "Asia/Kolkata";

const POLL_BOOKING_MS = 5000;
const POLL_CANCEL_MS = 8000;
const POLL_MAX = 72;                          // ~6 to 10 minutes, then "we'll email you"

/* ── Helpers ── */
const num = (v) => { const n = typeof v === "string" ? parseFloat(v) : v; return Number.isFinite(n) ? n : 0; };
const parse = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { const p = JSON.parse(v); return typeof p === "string" ? JSON.parse(p) : p; } catch { return null; }
};
const money = (n, currency = "INR") => {
  const v = num(n);
  if (!currency || currency === "INR") {
    return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  }
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(v); } catch { return `${currency} ${v}`; }
};
const humanize = (s) => { const t = String(s || "").replace(/_/g, " ").trim(); return t ? t[0].toUpperCase() + t.slice(1) : ""; };
const MEAL_LABEL = { Room_Only: "Room only", BreakFast: "Breakfast included", Breakfast_For_2: "Breakfast for 2", Half_Board: "Half board", Full_Board: "Full board", All_Inclusive: "All inclusive" };

/** TBO dates: "dd-mm-yyyy HH:mm:ss" / "dd/mm/yyyy ..." / ISO; no offset means UTC */
function parseTboDate(s) {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) { const [, d, mo, y, H = "0", M = "0", S = "0"] = m; return new Date(Date.UTC(+y, +mo - 1, +d, +H, +M, +S)); }
  const iso = String(s).trim();
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(" ", "T")}Z`);
  return isNaN(d) ? null : d;
}
const fmtIST = (d) => {
  if (!d) return "";
  try { return `${new Intl.DateTimeFormat("en-IN", { timeZone: IST, day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).format(d)} IST`; }
  catch { return d.toISOString(); }
};
const fmtDay = (iso, opts = { weekday: "short", day: "numeric", month: "short", year: "numeric" }) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", opts);
};
const nightsBetween = (a, b) => {
  try { return Math.max(1, Math.round((new Date(String(b).slice(0, 10)) - new Date(String(a).slice(0, 10))) / 86400000)); } catch { return 1; }
};
const todayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: IST });

const CHARGE_TYPE = { 1: "Fixed", 2: "Percentage", 3: "Night" };
function policyLines(pols) {
  const list = (pols || [])
    .map((p) => ({
      from: parseTboDate(p.FromDate ?? p.from_date),
      type: CHARGE_TYPE[p.ChargeType ?? p.charge_type] || String(p.ChargeType ?? p.charge_type ?? ""),
      charge: num(p.CancellationCharge ?? p.cancellation_charge),
      currency: p.Currency || "INR",
    }))
    .filter((p) => p.from)
    .sort((a, b) => a.from - b.from);
  return list.map((p, i) => ({ ...p, to: list[i + 1]?.from || null }));
}
function chargeText(p) {
  if (p.charge === 0) return "Free cancellation";
  if (/percent/i.test(p.type)) return p.charge >= 100 ? "100% of the booking amount (no refund)" : `${p.charge}% of the booking amount`;
  if (/night/i.test(p.type)) return `${p.charge} night${p.charge !== 1 ? "s" : ""} charged`;
  return `${money(Math.round(p.charge), p.currency)} cancellation charge`;
}
/** Charge if cancelled now, from the policy band in force */
function chargeNow(pols, hotelAmt, nights) {
  const lines = policyLines(pols);
  if (!lines.length) return null;
  const now = Date.now();
  const cur = [...lines].reverse().find((p) => p.from.getTime() <= now);
  if (!cur) return 0;
  if (/percent/i.test(cur.type)) return (hotelAmt * cur.charge) / 100;
  if (/night/i.test(cur.type)) return (hotelAmt / Math.max(1, nights)) * cur.charge;
  return cur.charge;
}

function safeHtml(html) {
  if (!html || typeof document === "undefined") return "";
  let s = String(html);
  if (/&lt;\s*\/?\w/.test(s)) { const t = document.createElement("textarea"); t.innerHTML = s; s = t.value; }
  const doc = new DOMParser().parseFromString(s, "text/html");
  doc.querySelectorAll("script,style,iframe,object,embed,link,meta,form,input,button,img,svg").forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((el) => { [...el.attributes].forEach((a) => el.removeAttribute(a.name)); });
  return doc.body.innerHTML.trim();
}

const parseBody = (raw) => (typeof raw?.body === "string" ? JSON.parse(raw.body) : raw);
async function postTBO(payload) {
  const r = await fetch(hotelOrchestratorUrl(HOTEL_TEST), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ hotel_test: HOTEL_TEST, ...payload }),
  });
  return parseBody(await r.json());
}

/* ── Status model ── */
const PENDING = ["PAYMENT_SUCCESS", "BOOKING_IN_PROGRESS", "BOOKING_PENDING", "VOUCHER_IN_PROGRESS"];
const HOLDING = ["HOLD_IN_PROGRESS", "HOLD_PENDING"];
const CANCELLING = ["CANCELLATION_REQUESTED", "CANCELLATION_VERIFYING", "CANCELLATION_IN_PROGRESS"];
const FAILED = ["BOOKING_FAILED", "VOUCHER_FAILED", "HOLD_FAILED", "PAYMENT_FAILED", "ORDER_FAILED"];

function statusView(row) {
  const st = row?.status || "";
  const paid = row?.payment_status === "PAID" || num(row?.refund_amount) > 0 || !!row?.payment_id;
  if (st === "BOOKING_CONFIRMED") {
    const cs = row.cancellation_status;
    const note = cs === "REJECTED" ? "The hotel didn't accept the cancellation request. Your booking stands. Contact us if you still want to cancel."
      : cs === "FAILED" ? "We couldn't cancel this booking online. Our team is taking it up with the hotel and will update you."
        : "";
    return { kind: "confirmed", tone: OK, icon: "check", title: "Your booking is confirmed", sub: `Confirmation sent to ${row.guest_email || "your email"}. Show this voucher at check-in.`, note };
  }
  if (PENDING.includes(st)) return { kind: "pending", tone: WARN, icon: "clock", poll: POLL_BOOKING_MS, title: "Confirming your booking with the hotel", sub: "Payment received. This usually takes a minute or two; this page updates by itself." };
  if (st === "HOLD_CONFIRMED") return { kind: "held", tone: INFO, icon: "clock", title: "Your room is reserved", sub: "Not paid yet. Pay before the deadline below to confirm your booking." };
  if (HOLDING.includes(st)) return { kind: "holding", tone: WARN, icon: "clock", poll: POLL_BOOKING_MS, title: "Reserving your room", sub: "We're confirming the reservation with the hotel. This page updates by itself." };
  if (CANCELLING.includes(st)) {
    const hold = row.is_hold === true && !paid;
    return { kind: "cancelling", tone: WARN, icon: "clock", poll: POLL_CANCEL_MS, title: hold ? "Releasing your reservation" : "Cancellation in progress", sub: st === "CANCELLATION_VERIFYING" ? "We're confirming the cancellation with the hotel. This takes about 2 minutes." : "The hotel is processing your cancellation. This page updates by itself." };
  }
  if (st === "CANCELLED") return { kind: "cancelled", tone: MUTED, icon: "x", title: "Booking cancelled", sub: "Your booking has been cancelled with the hotel." };
  if (st === "HOLD_RELEASED") return { kind: "released", tone: MUTED, icon: "x", title: "Reservation released", sub: "The room has been released. Nothing was charged." };
  if (st === "HOLD_EXPIRED") return { kind: "released", tone: MUTED, icon: "x", title: "Reservation expired", sub: "It wasn't paid by the deadline, so we released the room. Nothing was charged." };
  if (FAILED.includes(st)) return { kind: "failed", tone: BAD, icon: "x", title: "We couldn't complete this booking", sub: paid ? "Any amount you paid is being refunded to your original payment method within 5 to 7 working days." : "Nothing was charged." };
  return { kind: "unpaid", tone: MUTED, icon: "clock", title: "Payment not completed", sub: "This booking wasn't paid, so it isn't confirmed. You can book again from your trip." };
}

/* ── Styles ── */
const CSS = `
.rchc{font-family:'Inter',-apple-system,system-ui,'Segoe UI',Roboto,sans-serif;font-size:16px;line-height:1.5;color:${INK};-webkit-font-smoothing:antialiased;background:#F6F6F4;min-height:100vh}
.rchc *,.rchc *::before,.rchc *::after{box-sizing:border-box}
.rchc button,.rchc input,.rchc textarea{font:inherit;color:inherit}
.rchc-wrap{max-width:1100px;margin:0 auto;padding:20px 24px 80px}
.rchc-top{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px}
.rchc-link{background:none;border:0;padding:6px 0;cursor:pointer;color:${MUTED};font-size:15px;text-decoration:none}
.rchc-link:hover{color:${INK}}
.rchc-hero{background:#fff;border:1px solid ${LINE};border-radius:12px;padding:22px 24px;display:flex;gap:16px;align-items:flex-start;border-top:4px solid var(--tone)}
.rchc-hero h1{font-size:26px;line-height:1.2;margin:0;font-weight:700;letter-spacing:-.01em}
.rchc-hero p{margin:6px 0 0;color:${MUTED};font-size:15.5px}
.rchc-badge{width:44px;height:44px;border-radius:50%;flex:none;display:flex;align-items:center;justify-content:center;background:var(--tone);color:#fff}
.rchc-note{margin-top:12px;font-size:14.5px;background:#FFF4E5;border-left:3px solid ${WARN};padding:10px 12px;border-radius:4px}
.rchc-chip{display:inline-flex;align-items:center;gap:6px;margin-top:10px;font-size:13px;font-weight:600;padding:4px 10px;border-radius:99px;background:#EEF4EE;color:${OK}}
.rchc-chip.wait{background:#FFF4E5;color:${WARN}}
.rchc-ids{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:2px;background:${LINE};border:1px solid ${LINE};border-radius:12px;overflow:hidden;margin-top:14px}
.rchc-ids>div{background:#fff;padding:12px 16px;min-width:0}
.rchc-k{font-size:12px;text-transform:uppercase;letter-spacing:.07em;color:${MUTED};font-weight:600}
.rchc-v{font-size:16px;font-weight:600;margin-top:2px;overflow-wrap:anywhere}
.rchc-v.mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:15px}
.rchc-split{display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:20px;align-items:start;margin-top:20px}
.rchc-card{background:#fff;border:1px solid ${LINE};border-radius:12px;padding:20px 22px;margin-bottom:16px}
.rchc-card h2{font-size:18px;margin:0 0 12px;font-weight:700}
.rchc-hotel{display:flex;gap:16px;align-items:flex-start;justify-content:space-between}
.rchc-hotel h3{font-size:21px;margin:0;line-height:1.25}
.rchc-stars{color:#E0A100;letter-spacing:1px;font-size:14px}
.rchc-sub{color:${MUTED};font-size:14.5px;margin-top:4px}
.rchc-qr{width:96px;height:96px;flex:none;border:1px solid ${LINE};border-radius:8px;padding:4px;background:#fff}
.rchc-stay{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;border:1px solid ${LINE};border-radius:10px;margin-top:16px}
.rchc-stay>div{padding:14px 16px}
.rchc-stay .mid{text-align:center;color:${MUTED};font-size:13px;font-weight:600;border-left:1px solid ${LINE};border-right:1px solid ${LINE}}
.rchc-stay .big{font-size:18px;font-weight:700}
.rchc-row{display:flex;justify-content:space-between;gap:16px;padding:9px 0;border-top:1px solid ${LINE};font-size:15px}
.rchc-row:first-child{border-top:0}
.rchc-row>span:first-child{color:${MUTED}}
.rchc-row>span:last-child{text-align:right;font-weight:600}
.rchc-total{display:flex;justify-content:space-between;align-items:baseline;border-top:1px solid ${INK};margin-top:6px;padding-top:12px;font-weight:700}
.rchc-total b{font-size:22px}
.rchc-guests{margin:0;padding:0;list-style:none}
.rchc-guests li{padding:8px 0;border-top:1px solid ${LINE};display:flex;justify-content:space-between;gap:12px;font-size:15px}
.rchc-guests li:first-child{border-top:0}
.rchc-tag{font-size:12px;font-weight:600;color:${MUTED};background:#F1F1F1;border-radius:4px;padding:2px 6px}
.rchc-pols{margin:0;padding:0;list-style:none;font-size:14.5px}
.rchc-pols li{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:4px 16px;padding:9px 0;border-top:1px solid ${LINE}}
.rchc-pols li:first-child{border-top:0}
.rchc-pols .when{color:${MUTED}}
.rchc-pols .what{font-weight:600;text-align:right}
.rchc-pols li.now{background:#FFFBF2;margin:0 -10px;padding:9px 10px;border-radius:6px}
.rchc-hint{font-size:13px;color:${MUTED};margin-top:6px}
.rchc-steps{list-style:none;margin:4px 0 0;padding:0}
.rchc-steps li{position:relative;padding:0 0 18px 30px;font-size:15px}
.rchc-steps li::before{content:"";position:absolute;left:7px;top:20px;bottom:0;width:2px;background:${LINE}}
.rchc-steps li:last-child{padding-bottom:0}
.rchc-steps li:last-child::before{display:none}
.rchc-dot{position:absolute;left:0;top:3px;width:16px;height:16px;border-radius:50%;border:2px solid #C6C6C6;background:#fff}
.rchc-steps li.done .rchc-dot{background:${OK};border-color:${OK}}
.rchc-steps li.cur .rchc-dot{border-color:${WARN};box-shadow:0 0 0 3px #FFE9C7}
.rchc-steps li.bad .rchc-dot{background:${BAD};border-color:${BAD}}
.rchc-steps b{display:block}
.rchc-steps small{color:${MUTED};font-size:13.5px}
.rchc-supps td{padding:6px 0;border-top:1px solid ${LINE};font-size:14.5px;vertical-align:top}
.rchc-supps tr:first-child td{border-top:0}
.rchc-supps td:last-child{text-align:right;font-weight:600;white-space:nowrap;padding-left:12px}
.rchc-rich{font-size:14.5px;color:#3C4043}
.rchc-rich li{padding:3px 0}
.rchc-rich ul{margin:4px 0;padding-left:18px}
.rchc-btn{width:100%;height:50px;border:0;border-radius:8px;background:${ACCENT};color:#fff;font-weight:700;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px}
.rchc-btn:hover:not(:disabled){background:${ACCENT_DARK}}
.rchc-btn:disabled{background:#D5D5D5;cursor:not-allowed}
.rchc-btn2{width:100%;height:46px;border:1px solid #C6C6C6;border-radius:8px;background:#fff;font-weight:600;font-size:15px;cursor:pointer;margin-top:10px;display:flex;align-items:center;justify-content:center;gap:8px;color:${INK};text-decoration:none}
.rchc-btn2:hover{border-color:${INK}}
.rchc-btn2.danger{color:${BAD};border-color:#F0C5C2}
.rchc-btn2.danger:hover{border-color:${BAD}}
.rchc-err{font-size:14px;color:${BAD};background:#FCEEEE;border-radius:6px;padding:10px 12px;margin-top:12px}
.rchc-textlink{background:none;border:0;padding:0;cursor:pointer;color:${INK};font-weight:600;text-decoration:underline;text-underline-offset:3px;font-size:14.5px}
.rchc-cancel{border:1px solid #F0C5C2;background:#FFFBFB}
.rchc-reasons{display:grid;gap:8px;margin:10px 0}
.rchc-reasons label{display:flex;gap:10px;align-items:center;font-size:15px;cursor:pointer}
.rchc-reasons input{accent-color:${ACCENT};width:17px;height:17px}
.rchc-ta{width:100%;min-height:70px;border:1px solid #C6C6C6;border-radius:6px;padding:10px 12px;font-size:15px;resize:vertical}
.rchc-est{background:#fff;border:1px solid ${LINE};border-radius:8px;padding:6px 14px;margin:12px 0}
.rchc-spin{animation:rchc-spin .9s linear infinite;display:block}
@keyframes rchc-spin{to{transform:rotate(360deg)}}
.rchc-state{max-width:480px;margin:80px auto;text-align:center;padding:0 16px}
.rchc-state h2{font-size:24px;margin:16px 0 8px}
.rchc-state p{color:${MUTED};margin:0}
@media (max-width:900px){
  .rchc-split{grid-template-columns:1fr}
  .rchc-split.act-first>aside{order:-1}   /* paying for a hold / cancelling: that comes first on mobile */
}
@media (max-width:640px){
  .rchc-wrap{padding:14px 16px 60px}
  .rchc-hero{padding:18px}
  .rchc-hero h1{font-size:22px}
  .rchc-hotel{flex-direction:column}
  .rchc-qr{width:84px;height:84px}
  .rchc-stay{grid-template-columns:1fr 1fr}
  .rchc-stay .mid{grid-column:1/-1;grid-row:1;border:0;border-bottom:1px solid ${LINE};padding:8px}
  .rchc-pols li{grid-template-columns:1fr}
  .rchc-pols .what{text-align:left}
}
@media print{
  nav,header,.rchc-noprint{display:none !important}
  .rchc{background:#fff}
  .rchc-wrap{padding:0;max-width:none}
  .rchc-split{grid-template-columns:1fr 300px}
  .rchc-card,.rchc-hero,.rchc-ids{break-inside:avoid;box-shadow:none}
}
`;

/* ── Small pieces ── */
function Spin({ size = 20, color = ACCENT, track = "#E6E6E6" }) {
  return (
    <svg viewBox="0 0 24 24" className="rchc-spin" style={{ width: size, height: size }} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke={track} strokeWidth="2.5" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
const Icon = ({ name }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === "check" && <path d="M5 12.5l4.5 4.5L19 7.5" />}
    {name === "x" && <path d="M7 7l10 10M17 7L7 17" />}
    {name === "clock" && <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>}
  </svg>
);

function PolicyList({ policies, refundable, lcd, highlightNow }) {
  const lines = policyLines(policies);
  const now = Date.now();
  if (!lines.length) {
    return <p style={{ margin: 0, fontSize: 15 }}>{refundable === false ? <span style={{ color: BAD }}>Non-refundable. No refund on cancellation or no-show.</span> : lcd ? <>Free cancellation until <b>{fmtIST(lcd)}</b>.</> : "See the rate conditions below."}</p>;
  }
  return (
    <ul className="rchc-pols">
      {lines.map((p, i) => {
        const isNow = highlightNow && p.from.getTime() <= now && (!p.to || p.to.getTime() > now);
        return (
          <li key={i} className={isNow ? "now" : ""}>
            <span className="when">{p.to ? <>From {fmtIST(p.from)} until {fmtIST(p.to)}</> : <>From {fmtIST(p.from)} onwards</>}{isNow && <b style={{ color: WARN }}> · applies now</b>}</span>
            <span className="what" style={p.charge === 0 ? { color: OK } : null}>{chargeText(p)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Cancellation / refund progress, industry style */
function RefundTracker({ row, view }) {
  const st = row.status;
  const rs = row.refund_status || "";
  const cr = parse(row.change_request_response) || {};
  const holdNoPay = row.refund_status === "NOT_APPLICABLE";
  const processed = st === "CANCELLED" || ["HOLD_RELEASED", "HOLD_EXPIRED"].includes(st) || row.change_request_status === "PROCESSED";
  const refundStarted = ["REFUND_IN_PROGRESS", "REFUND_COMPLETED"].includes(rs);
  const refundDone = rs === "REFUND_COMPLETED";
  const verified = !!row.cancel_detail_checked_at && /cancel/i.test(row.tbo_booking_status || "");
  const amount = num(row.refund_amount);
  const charge = row.cancellation_charge != null && row.cancellation_charge !== "" ? num(row.cancellation_charge) : null;

  const steps = [
    { label: "Cancellation requested", sub: row.cancellation_requested_at ? fmtIST(new Date(row.cancellation_requested_at)) : row.change_request_id ? `Request ID ${row.change_request_id}` : "", state: "done" },
    { label: processed ? "Cancelled by the hotel" : "Hotel processing", sub: st === "CANCELLATION_VERIFYING" ? "Confirming with the hotel" : processed && row.change_request_id ? `Request ID ${row.change_request_id}` : "", state: processed ? "done" : "cur" },
  ];
  if (!holdNoPay) {
    steps.push({
      label: rs === "REFUND_FAILED" ? "Refund needs attention" : refundStarted ? `Refund of ${money(amount)} initiated` : "Refund",
      sub: rs === "REFUND_FAILED" ? "Our team will process it manually and contact you." : refundStarted ? "To your original payment method" : processed ? "Our team is processing the refund" : "Starts once the hotel confirms",
      state: rs === "REFUND_FAILED" ? "bad" : refundStarted ? "done" : processed ? "cur" : "",
    });
    steps.push({ label: refundDone ? "Refund credited" : "Refund credited", sub: refundDone ? "" : "Usually 5 to 7 working days after it's initiated", state: refundDone ? "done" : "" });
  }

  return (
    <div className="rchc-card">
      <h2>{holdNoPay ? "Reservation release" : "Cancellation and refund"}</h2>
      <ul className="rchc-steps">
        {steps.map((s, i) => (
          <li key={i} className={s.state}>
            <span className="rchc-dot" /><b>{s.label}</b>{s.sub && <small>{s.sub}</small>}
          </li>
        ))}
      </ul>
      {(charge !== null || amount > 0 || cr.CreditNoteNo) && (
        <div style={{ marginTop: 14 }}>
          {charge !== null && <div className="rchc-row"><span>Cancellation charge</span><span>{money(charge)}</span></div>}
          {amount > 0 && <div className="rchc-row"><span>Refund amount</span><span>{money(amount)}</span></div>}
          {cr.CreditNoteNo && <div className="rchc-row"><span>Credit note</span><span>{cr.CreditNoteNo}</span></div>}
        </div>
      )}
      {!holdNoPay && num(row.rc_platform_fee) > 0 && <div className="rchc-hint">The platform fee and GST on it aren't refundable.</div>}
      {verified && <div className="rchc-chip">✓ Verified with the hotel {row.cancel_detail_checked_at ? `on ${fmtIST(new Date(row.cancel_detail_checked_at))}` : ""}</div>}
      {view.kind === "cancelling" && <div className="rchc-chip wait"><Spin size={13} color={WARN} /> Updating automatically</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════ */
export default function HotelConfirmationPage() {
  const { rcBookingId } = useParams();
  const rcId = decodeURIComponent(rcBookingId || "");
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const partnerBrand = usePartnerBrand();

  const owner = useMemo(() => {
    let stored = "";
    try { stored = sessionStorage.getItem(OWNER_KEY + rcId) || ""; } catch { /* blocked */ }
    let login = "";
    try { login = getCurrentContact() || ""; } catch { /* not signed in */ }
    return location.state?.owner || stored || login;
  }, [rcId, location.state]);

  const [row, setRow] = useState(null);
  const [state, setState] = useState("loading");   // loading | ready | notfound | error
  const [cancelOpen, setCancelOpen] = useState(search.get("cancel") === "1");
  const [reason, setReason] = useState("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState("");             // "" | "cancel" | "pay"
  const [err, setErr] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [allRc, setAllRc] = useState(false);
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const body = await postTBO({ intent: "get_status", From: `user:${owner}`, rc_booking_id: rcId });
      const bk = body?.data?.bookings?.[0];
      if (body?.status === 404 || !bk) { setState((s) => (s === "ready" ? s : "notfound")); return null; }
      setRow(bk); setState("ready");
      return bk;
    } catch (e) {
      console.error("[HotelConfirmation] get_status failed", e);
      setState((s) => (s === "ready" ? s : "error"));
      return null;
    }
  }, [owner, rcId]);

  useEffect(() => { if (rcId) load(); }, [rcId, load]);

  const view = useMemo(() => (row ? statusView(row) : null), [row]);
  // A TBO check is still scheduled (e.g. GetBookingDetail 120s after a cancellation)
  const checking = !!row && !!row.verify_kind && num(row.verify_after) > 0;

  // Poll while something is still happening at the hotel
  useEffect(() => {
    if (!row || !view) return undefined;
    if (!view.poll && !checking) return undefined;
    if (polls.current >= POLL_MAX) return undefined;
    const t = setTimeout(async () => {
      polls.current += 1;
      if (row.status === "CANCELLATION_IN_PROGRESS" && row.change_request_id) {
        await postTBO({ intent: "hotel_cancellation_status", From: `user:${owner}`, rc_booking_id: rcId }).catch(() => null);
      }
      load();
    }, view.poll || POLL_CANCEL_MS);
    return () => clearTimeout(t);
  }, [row, view, checking, owner, rcId, load]);

  /* ── Derived booking data ── */
  const d = useMemo(() => parse(row?.booking_detail) || {}, [row]);
  const pb = useMemo(() => parse(row?.prebook_data) || {}, [row]);
  const req = useMemo(() => parse(row?.booking_request) || {}, [row]);
  const pbHotel = pb.HotelResult?.[0] || {};
  const pbRoom = pbHotel.Rooms?.[0] || {};
  const dRoom = d.Rooms?.[0] || {};

  const rateConds = useMemo(() => {
    const list = (d.RateConditions?.length ? d.RateConditions : pbHotel.RateConditions) || [];
    return (Array.isArray(list) ? list : [list]).map(safeHtml).filter(Boolean);
  }, [d, pbHotel]);
  const findCond = (re) => {
    const raw = (d.RateConditions || pbHotel.RateConditions || []).find((c) => re.test(String(c)));
    return raw ? String(raw).replace(re, "").trim() : "";
  };

  if (!rcId || state === "notfound" || state === "error") {
    return (
      <>
        <Navbar />
        <style>{CSS}</style>
        <div className="rchc" style={{ paddingTop: 70 }}>
          <div className="rchc-state">
            <h2>{state === "error" ? "We couldn't load this booking" : "Booking not found"}</h2>
            <p>{state === "error" ? "Please check your connection and try again." : owner ? "We couldn't find this booking under your account." : "Please sign in with the email or phone you booked with to see this booking."}</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22 }}>
              {state === "error" && <button className="rchc-btn" style={{ width: "auto", padding: "0 22px" }} onClick={() => { setState("loading"); load(); }}>Try again</button>}
              <Link className="rchc-btn2" style={{ width: "auto", padding: "0 22px", marginTop: 0 }} to="/booking-history">Booking history</Link>
            </div>
          </div>
        </div>
      </>
    );
  }
  if (state === "loading" || !row || !view) {
    return (
      <>
        <Navbar />
        <style>{CSS}</style>
        <div className="rchc" style={{ paddingTop: 70 }}>
          <div className="rchc-state"><div style={{ display: "flex", justifyContent: "center" }}><Spin size={40} /></div><p style={{ marginTop: 16 }}>Loading your booking</p></div>
        </div>
      </>
    );
  }

  const hotelName = row.hotel_name || d.HotelName || "Hotel";
  const stars = Math.min(5, Math.round(num(d.StarRating)));
  const address = [d.AddressLine1, d.AddressLine2, d.City].filter(Boolean).join(", ");
  const lat = num(d.Latitude), lng = num(d.Longitude);
  const checkIn = row.check_in_date || String(d.CheckInDate || "").slice(0, 10);
  const checkOut = row.check_out_date || String(d.CheckOutDate || "").slice(0, 10);
  const nights = nightsBetween(checkIn, checkOut);
  const ciTime = findCond(/^\s*CheckIn Time-Begin:\s*/i);
  const coTime = findCond(/^\s*CheckOut Time:\s*/i);
  const rooms = Math.max(1, num(row.num_rooms) || num(d.NoOfRooms) || 1);
  const roomName = row.room_type_name || dRoom.RoomTypeName || (Array.isArray(pbRoom.Name) ? pbRoom.Name.join(", ") : pbRoom.Name) || "";
  const meal = MEAL_LABEL[pbRoom.MealType] || humanize(pbRoom.MealType) || "";
  const inclusions = String(dRoom.Inclusion || pbRoom.Inclusion || "").split(",").map((x) => x.trim()).filter(Boolean);
  const promos = (dRoom.RoomPromotion || pbRoom.RoomPromotion || []).filter(Boolean);

  // Guests: TBO's record (GetBookingDetail) first, else what we sent
  const guestRooms = (d.Rooms?.length ? d.Rooms.map((r) => r.HotelPassenger || []) : (req.RoomsGuests || [req.Guests || []]))
    .map((list) => (list || []).map((g) => ({ name: [String(g.Title || "").replace(/\.$/, ""), g.FirstName, g.LastName].filter(Boolean).join(" "), lead: !!g.LeadPassenger, child: Number(g.PaxType) === 2 })));
  const adults = num(row.num_adults) || guestRooms.flat().filter((g) => !g.child).length;

  const policies = parse(row.cancel_policies) || dRoom.CancelPolicies || pbRoom.CancelPolicies || [];
  const lcd = parseTboDate(row.last_cancellation_deadline || d.LastCancellationDeadline || pbRoom.LastCancellationDeadline);
  const refundable = pbRoom.IsRefundable;

  const supps = (Array.isArray(dRoom.Supplements) ? dRoom.Supplements : (pbRoom.Supplements || []).flat(3))
    .filter((x) => x && typeof x === "object")
    .map((x) => ({ type: String(x.Type ?? ""), desc: humanize(x.Description) || "Charge", price: num(x.Price), currency: x.Currency || "INR" }))
    .filter((s) => !/^included$/i.test(s.type));

  const paid = num(row.amount);
  const hotelAmt = num(row.hotel_amount) || num(pbRoom.TotalFare);
  const hotelTax = num(pbRoom.TotalTax);
  const fee = num(row.rc_platform_fee);
  const gst = Math.round(fee * num(row.rc_gst_rate)) / 100;
  const isPaid = row.payment_status === "PAID" || ["CANCELLED", "BOOKING_CONFIRMED", ...PENDING].includes(row.status) && !!row.payment_id;

  const confNo = row.booking_confirmation_no || row.hold_confirmation_no || d.ConfirmationNo || "";
  const qr = typeof d.QRCode === "string" && d.QRCode.startsWith("data:image/") ? d.QRCode : "";
  const bookedOn = d.BookingDate ? parseTboDate(d.BookingDate) : row.created_at ? new Date(row.created_at) : null;
  const payBy = parseTboDate(row.hold_pay_by);

  const upcoming = checkIn && checkIn >= todayISO();
  const canCancel = ["BOOKING_CONFIRMED", "HOLD_CONFIRMED"].includes(row.status) && upcoming;
  const isHold = row.status === "HOLD_CONFIRMED";
  const estCharge = canCancel && !isHold ? chargeNow(policies, hotelAmt, nights) : null;
  const estRefund = estCharge === null ? null : Math.max(0, paid - fee - gst - estCharge);
  const showTracker = ["cancelling", "cancelled"].includes(view.kind) || (view.kind === "released" && row.change_request_id);

  const closeCancel = () => { setCancelOpen(false); setErr(""); if (search.get("cancel")) { search.delete("cancel"); setSearch(search, { replace: true }); } };

  async function submitCancel() {
    const text = reason === "Other" ? other.trim() : reason;
    if (!text || text.length < 3) { setErr(reason === "Other" ? "Please tell us a little about why you're cancelling." : "Please pick a reason."); return; }
    setErr(""); setBusy("cancel");
    try {
      const res = await postTBO({ intent: "cancel_hotel_booking", From: `webbot_new:${owner}`, rc_booking_id: rcId, Remarks: text.slice(0, 450) });
      const st = res?.data?.status;
      if (st === "REJECTED") throw new Error(res?.data?.message || "The hotel didn't accept this cancellation. Please contact support.");
      if (res?.status !== 200 && res?.status !== 202) throw new Error(res?.message || "We couldn't cancel this booking. Please try again.");
      closeCancel();
      polls.current = 0;
      await load();
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy("");
    }
  }

  async function payHold() {
    setErr(""); setBusy("pay");
    try {
      const ord = await postTBO({ intent: "create_order_hotel", From: `user:${owner}`, rc_booking_id: rcId, ClientReferenceId: rcId, lead_email: row.guest_email });
      if (ord?.status !== 200) throw new Error(ord?.message || "We couldn't start the payment. Please try again.");
      const o = ord.data || {};
      await new Promise((res, rej) => {
        if (window.Razorpay) return res();
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = res;
        s.onerror = () => rej(new Error("Couldn't load the payment window. Check your connection and try again."));
        document.body.appendChild(s);
      });
      new window.Razorpay({
        key: o.rzp_key_id, amount: Math.round(num(o.amount) * 100), currency: o.currency || "INR", order_id: o.rzp_order_id,
        name: "RouteChef", description: `${hotelName}, ${fmtDay(checkIn, { day: "numeric", month: "short" })}`,
        prefill: { name: row.guest_name, email: row.guest_email, contact: row.guest_phone },
        theme: { color: ACCENT },
        handler: () => { polls.current = 0; setRow((r) => ({ ...r, status: "PAYMENT_SUCCESS", payment_status: "PAID" })); setTimeout(load, 1500); },
        modal: { ondismiss: () => setBusy("") },
      }).open();
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy("");
    }
  }

  const rcShown = allRc ? rateConds : rateConds.slice(0, 5);
  const supportPrefill = (() => {
    const [firstName = "", ...rest] = String(row.guest_name || "").trim().split(/\s+/);
    return {
      firstName, lastName: rest.join(" "), email: row.guest_email || "", phone: row.guest_phone || "",
      message: `I need help with my hotel booking at ${hotelName} (check-in ${checkIn}, booking ID ${rcId}).`,
    };
  })();

  return (
    <>
      <Navbar />
      <style>{CSS}</style>
      <div className="rchc" style={{ paddingTop: 70 }}>
        <div className="rchc-wrap">
          {partnerBrand && <div className="rchc-noprint" style={{ marginBottom: 12 }}><CoBrandBar brand={partnerBrand} variant="slim" /></div>}
          <div className="rchc-top rchc-noprint">
            <Link className="rchc-link" to="/booking-history">← Booking history</Link>
            {["confirmed", "cancelled", "held"].includes(view.kind) && <button className="rchc-textlink" onClick={() => window.print()}>Print or save as PDF</button>}
          </div>

          {/* Status */}
          <section className="rchc-hero" style={{ "--tone": view.tone }}>
            <div className="rchc-badge">{view.poll ? <Spin size={22} color="#fff" track="rgba(255,255,255,.35)" /> : <Icon name={view.icon} />}</div>
            <div style={{ minWidth: 0 }}>
              <h1>{view.title}</h1>
              <p>{view.sub}</p>
              {view.kind === "held" && payBy && <p style={{ color: INK }}>Pay by <b>{fmtIST(payBy)}</b>. If it isn't paid by then, we release the room at no cost.</p>}
              {view.note && <div className="rchc-note">{view.note}</div>}
              {checking && !view.poll && <div className="rchc-chip wait"><Spin size={13} color={WARN} /> Confirming the final status with the hotel</div>}
              {polls.current >= POLL_MAX && (view.poll || checking) && <div className="rchc-note">This is taking longer than usual. We'll email you the final status, and this page will show it too.</div>}
            </div>
          </section>

          {/* References */}
          <section className="rchc-ids">
            <div><div className="rchc-k">Booking ID</div><div className="rchc-v mono">{rcId}</div></div>
            {confNo && <div><div className="rchc-k">{isHold ? "Reservation no." : "Hotel confirmation no."}</div><div className="rchc-v mono">{confNo}</div></div>}
            {d.TBOReferenceNo && <div><div className="rchc-k">Supplier reference</div><div className="rchc-v mono">{d.TBOReferenceNo}</div></div>}
            {bookedOn && <div><div className="rchc-k">Booked on</div><div className="rchc-v">{fmtIST(bookedOn)}</div></div>}
          </section>

          <div className={`rchc-split${view.kind === "held" || cancelOpen ? " act-first" : ""}`}>
            <main>
              {showTracker && <RefundTracker row={row} view={view} />}

              {/* Hotel + stay */}
              <section className="rchc-card">
                <div className="rchc-hotel">
                  <div style={{ minWidth: 0 }}>
                    <h3>{hotelName}</h3>
                    {stars > 0 && <div className="rchc-stars" aria-label={`${stars}-star hotel`}>{"★".repeat(stars)}</div>}
                    {address && <div className="rchc-sub">{address}</div>}
                    {(lat || lng) ? <a className="rchc-textlink rchc-noprint" style={{ display: "inline-block", marginTop: 6 }} href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noopener noreferrer">Get directions</a> : null}
                  </div>
                  {qr && view.kind === "confirmed" && <img className="rchc-qr" src={qr} alt="Booking QR code" />}
                </div>
                <div className="rchc-stay">
                  <div><div className="rchc-k">Check-in</div><div className="big">{fmtDay(checkIn)}</div>{ciTime && <div className="rchc-sub">From {ciTime}</div>}</div>
                  <div className="mid">{nights} night{nights !== 1 ? "s" : ""}</div>
                  <div><div className="rchc-k">Check-out</div><div className="big">{fmtDay(checkOut)}</div>{coTime && <div className="rchc-sub">Until {coTime}</div>}</div>
                </div>
                <div style={{ marginTop: 14 }}>
                  <div className="rchc-row"><span>Room</span><span>{rooms > 1 ? `${rooms} × ` : ""}{roomName}</span></div>
                  {meal && <div className="rchc-row"><span>Meal plan</span><span>{meal}</span></div>}
                  {inclusions.length > 0 && <div className="rchc-row"><span>Includes</span><span>{inclusions.join(", ")}</span></div>}
                  {promos.length > 0 && <div className="rchc-row"><span>Offer</span><span>{promos.join(", ")}</span></div>}
                  <div className="rchc-row"><span>Guests</span><span>{adults} adult{adults !== 1 ? "s" : ""}, {rooms} room{rooms !== 1 ? "s" : ""}</span></div>
                </div>
              </section>

              {/* Guests */}
              {guestRooms.some((r) => r.length) && (
                <section className="rchc-card">
                  <h2>Guest details</h2>
                  {guestRooms.map((list, ri) => (
                    <div key={ri} style={{ marginTop: ri ? 12 : 0 }}>
                      {guestRooms.length > 1 && <div className="rchc-k" style={{ marginBottom: 4 }}>Room {ri + 1}</div>}
                      <ul className="rchc-guests">
                        {list.map((g, gi) => (
                          <li key={gi}><span>{g.name}</span><span>{g.lead ? <span className="rchc-tag">Lead guest</span> : g.child ? <span className="rchc-tag">Child</span> : null}</span></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {(row.guest_email || row.guest_phone) && <div className="rchc-hint">Contact: {[row.guest_email, row.guest_phone].filter(Boolean).join(" · ")}</div>}
                </section>
              )}

              {/* Cancellation policy */}
              {!["cancelled", "released", "failed", "unpaid"].includes(view.kind) && (
                <section className="rchc-card">
                  <h2>Cancellation policy</h2>
                  <PolicyList policies={policies} refundable={refundable} lcd={lcd} highlightNow={canCancel} />
                  {lcd && <div className="rchc-hint">Last date to cancel without charge: <b>{fmtIST(lcd)}</b>{lcd.getTime() < Date.now() ? " (passed)" : ""}.</div>}
                  <div className="rchc-hint">All times are in Indian Standard Time (IST, UTC+05:30). No-shows are charged in full.</div>
                </section>
              )}

              {/* Pay at hotel */}
              {supps.length > 0 && !["cancelled", "released", "failed"].includes(view.kind) && (
                <section className="rchc-card">
                  <h2>Payable at the hotel</h2>
                  <table className="rchc-supps" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>{supps.map((s, i) => <tr key={i}><td>{s.desc}</td><td>{money(s.price, s.currency)}</td></tr>)}</tbody>
                  </table>
                  <div className="rchc-hint">Not included in the amount you paid online. Paid directly to the hotel at check-in, in the currency shown.</div>
                </section>
              )}

              {/* Important information */}
              {rateConds.length > 0 && !["failed", "unpaid"].includes(view.kind) && (
                <section className="rchc-card">
                  <h2>Important information</h2>
                  <ul className="rchc-rich" style={{ margin: 0, paddingLeft: 18 }}>
                    {rcShown.map((h, i) => <li key={i} dangerouslySetInnerHTML={{ __html: h }} />)}
                  </ul>
                  {rateConds.length > 5 && (
                    <button className="rchc-textlink rchc-noprint" style={{ marginTop: 8 }} onClick={() => setAllRc((o) => !o)}>
                      {allRc ? "Show less" : `Show all ${rateConds.length}`}
                    </button>
                  )}
                </section>
              )}
            </main>

            <aside>
              {/* Payment */}
              <section className="rchc-card">
                <h2>{isPaid ? "Payment summary" : "Price summary"}</h2>
                <div className="rchc-row"><span>Room charges ({nights} night{nights !== 1 ? "s" : ""})</span><span>{money(Math.round((hotelAmt - hotelTax) * 100) / 100)}</span></div>
                {hotelTax > 0 && <div className="rchc-row"><span>Hotel taxes</span><span>{money(hotelTax)}</span></div>}
                {fee > 0 && <div className="rchc-row"><span>Platform fee</span><span>{money(fee)}</span></div>}
                {gst > 0 && <div className="rchc-row"><span>GST on platform fee</span><span>{money(gst)}</span></div>}
                <div className="rchc-total"><span>{isPaid ? "Total paid" : view.kind === "held" ? "To pay to confirm" : "Total"}</span><b>{money(paid)}</b></div>
                {isPaid && row.payment_id && <div className="rchc-hint">Payment ID {row.payment_id}</div>}
                {row.tbo_invoice_no && <div className="rchc-hint">Supplier invoice {row.tbo_invoice_no}</div>}
              </section>

              {/* Actions */}
              <section className="rchc-card rchc-noprint">
                {view.kind === "held" && (
                  <button className="rchc-btn" disabled={!!busy || (payBy && payBy.getTime() < Date.now())} onClick={payHold}>
                    {busy === "pay" && <Spin size={18} color="#fff" track="rgba(255,255,255,.35)" />}
                    {payBy && payBy.getTime() < Date.now() ? "Pay-by time has passed" : `Pay ${money(paid)} & confirm`}
                  </button>
                )}
                {["confirmed", "cancelled", "held"].includes(view.kind) && (
                  <button className={view.kind === "held" ? "rchc-btn2" : "rchc-btn"} onClick={() => window.print()}>{view.kind === "confirmed" ? "Download voucher" : view.kind === "held" ? "Download reservation" : "Download cancellation details"}</button>
                )}
                {canCancel && !cancelOpen && (
                  <button className="rchc-btn2 danger" onClick={() => { setCancelOpen(true); setErr(""); }}>{isHold ? "Release reservation" : "Cancel booking"}</button>
                )}
                {["failed", "unpaid", "released"].includes(view.kind) && <Link className="rchc-btn" to="/trips">Plan another stay</Link>}
                <button className="rchc-btn2" onClick={() => setHelpOpen(true)}>Need help?</button>
                {err && !cancelOpen && <div className="rchc-err">{err}</div>}
              </section>

              {/* Cancel panel */}
              {cancelOpen && canCancel && (
                <section className="rchc-card rchc-cancel rchc-noprint">
                  <h2>{isHold ? "Release this reservation?" : "Cancel this booking?"}</h2>
                  {isHold ? (
                    <p style={{ margin: 0, fontSize: 15 }}>Nothing has been charged, so there's nothing to refund. You'll need to search again to book this hotel.</p>
                  ) : (
                    <div className="rchc-est">
                      <div className="rchc-row"><span>Amount paid</span><span>{money(paid)}</span></div>
                      {estCharge !== null && <div className="rchc-row"><span>Cancellation charge now</span><span style={{ color: estCharge > 0 ? BAD : OK }}>{estCharge > 0 ? money(Math.round(estCharge)) : "Free"}</span></div>}
                      {fee > 0 && <div className="rchc-row"><span>Platform fee + GST (not refundable)</span><span>{money(fee + gst)}</span></div>}
                      {estRefund !== null && <div className="rchc-row"><span>Estimated refund</span><span>{money(Math.round(estRefund))}</span></div>}
                    </div>
                  )}
                  {!isHold && <div className="rchc-hint" style={{ marginTop: 0 }}>The final charge and refund are confirmed by the hotel.</div>}
                  <div className="rchc-k" style={{ marginTop: 14 }}>Reason</div>
                  <div className="rchc-reasons" role="radiogroup">
                    {["Change of plans", "Found a better option", "Booked by mistake", "Other"].map((r) => (
                      <label key={r}><input type="radio" name="rchc-reason" checked={reason === r} onChange={() => setReason(r)} />{r}</label>
                    ))}
                  </div>
                  {reason === "Other" && <textarea className="rchc-ta" value={other} maxLength={450} placeholder="Tell us why" onChange={(e) => setOther(e.target.value)} />}
                  {err && <div className="rchc-err">{err}</div>}
                  <button className="rchc-btn" style={{ background: BAD, marginTop: 12 }} disabled={!!busy || !reason} onClick={submitCancel}>
                    {busy === "cancel" && <Spin size={18} color="#fff" track="rgba(255,255,255,.35)" />}
                    {busy === "cancel" ? (isHold ? "Releasing" : "Cancelling") : (isHold ? "Yes, release it" : "Yes, cancel booking")}
                  </button>
                  <button className="rchc-btn2" disabled={!!busy} onClick={closeCancel}>Keep my booking</button>
                </section>
              )}
            </aside>
          </div>
        </div>
      </div>
      <ContactSupportModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} bookingId={rcId} prefillData={supportPrefill} />
    </>
  );
}
